import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { BehaviorSubject } from 'rxjs';
import { User } from 'src/app/models/user.interface';
import { Collection } from 'src/app/models/collection.interface';
import { MatChipInputEvent } from '@angular/material/chips';

@Component({
  selector: 'app-edit-collection-dialog',
  templateUrl: './edit-collection-dialog.component.html',
  styleUrls: ['./edit-collection-dialog.component.scss']
})
export class EditCollectionDialogComponent implements OnInit {
  addCollectionForm!: FormGroup;
  users: User[] = [];
  collection: Collection;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  private filteredUsersSubject = new BehaviorSubject<{ user_id: string; email: string }[]>([]);
  filteredUsers$ = this.filteredUsersSubject.asObservable();
  searchControl = new FormControl();

  constructor(
    public dialogRef: MatDialogRef<EditCollectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditCollectionDialogComponent,
    private fb: FormBuilder
  ) {
    this.users = data.users;
    this.collection = data.collection;
  }

  ngOnInit(): void {
    const initialDimensions = this.collection.dimensions;
    const accessList: string[] = this.collection.access_list ?? [];
    this.addCollectionForm = this.fb.group({
      name: new FormControl(this.collection.name),
      description: new FormControl(this.collection.description),
      private: new FormControl(this.collection.access_list.length > 0),
      access_list: this.fb.array(accessList.map(id => this.fb.control(id))),
      dimensions: new FormArray(
        initialDimensions.map(dim =>
          this.fb.group({
            name: new FormControl({ value: dim.name, disabled: true }),
            description: new FormControl({ value: dim.description, disabled: true })
          })
        )
      ),
      id: new FormControl(this.collection.id)
    });

    this.searchControl.valueChanges.subscribe(value => {
      this.filteredUsersSubject.next(this._filterUsers(value));
    });
  }

  displayUser(user: User): string {
    return user ? user.email : '';
  }

  addAccessFromInput(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value.trim();

    if (value) {
      const user = this.users.find(u => u.email.toLowerCase() === value.toLowerCase());
      if (user && !this.accessList.value.includes(user.user_id)) {
        this.accessList.push(new FormControl(user.user_id));
      }
    }

    // Clear the input field and reset the search control
    if (input) {
      input.value = '';
    }
    this.searchControl.setValue(null);
  }

  get accessList(): FormArray {
    return this.addCollectionForm.get('access_list') as FormArray;
  }

  get dimensions(): FormArray {
    return this.addCollectionForm.get('dimensions') as FormArray;
  }

  private _filterUsers(value: string): { user_id: string; email: string }[] {
    // Ensure the value is a string
    const filterValue = value ? value.toString().toLowerCase() : '';
    return this.users.filter(user => user.email.toLowerCase().includes(filterValue));
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  saveForm(): void {
    this.dimensions.controls.forEach(control => control.enable());
    const formValue = this.addCollectionForm.value;

    this.dialogRef.close(formValue);
  }

  addAccess(user: { user_id: string; email: string }): void {
    if (!this.accessList.value.includes(user.user_id)) {
      this.accessList.push(new FormControl(user.user_id));
    }
    this.searchControl.setValue('');
  }

  removeAccess(user: string): void {
    const index = this.accessList.controls.findIndex(control => control.value === user);
    if (index >= 0) {
      this.accessList.removeAt(index);
    }
  }

  addDimension(): void {
    this.dimensions.insert(
      0,
      this.fb.group({
        name: new FormControl(''),
        description: new FormControl('')
      })
    );
  }

  removeDimension(index: number): void {
    this.dimensions.removeAt(index);
  }

  getEmail(user_id: string): string | undefined {
    return this.users.find(user => user.user_id === user_id)?.email;
  }
}
